<template>
  <el-dialog
    :title="isEdit ? 'Edit Notice' : 'Add Notice'"
    :visible.sync="dialogVisible"
    width="720px"
    top="4vh"
    custom-class="notice-form-dialog"
    append-to-body
    :close-on-click-modal="false"
  >
    <div class="notice-form">
      <div class="notice-form__field">
        <label class="notice-form__label">Title <span class="text-danger">*</span></label>
        <el-input v-model="form.title" maxlength="150" show-word-limit placeholder="Enter notice title"></el-input>
        <p v-if="shownErrors.title" class="notice-form__field-error">{{ shownErrors.title }}</p>
      </div>

      <div class="notice-form__field">
        <label class="notice-form__label">Content <span class="text-danger">*</span></label>
        <editor v-if="dialogVisible" :key="editorKey" v-model="form.content" :init="tinymceInit" />
        <p v-if="shownErrors.content" class="notice-form__field-error">{{ shownErrors.content }}</p>
      </div>

      <div class="row">
        <div class="col-md-6 notice-form__field">
          <label class="notice-form__label">Type</label>
          <el-select v-model="form.type" class="w-100" popper-class="notice-form__type-popper">
            <el-option v-for="type in types" :key="type.value" :value="type.value" :label="type.label">
              <span class="notice-form__type-option">
                <span class="notice-form__dot" :class="'notice-form__dot--' + type.value"></span>
                <strong>{{ type.label }}</strong>
                <span class="notice-form__type-effect">&mdash; {{ type.effect }}</span>
              </span>
            </el-option>
          </el-select>
          <p class="notice-form__hint" :class="{ 'notice-form__hint--critical': form.type === 'critical' }">
            <i class="fas fa-info-circle"></i> {{ typeHint }}
          </p>
        </div>
        <div class="col-md-6 notice-form__field">
          <label class="notice-form__label">Target <span class="text-danger">*</span></label>
          <div class="notice-form__checkboxes">
            <el-checkbox v-model="form.target_agency" border>Agency</el-checkbox>
            <el-checkbox v-model="form.target_client" border>Client</el-checkbox>
          </div>
          <!-- error menggantikan hint sementara, supaya kolom tetap sejajar dengan Type -->
          <p v-if="shownErrors.target" class="notice-form__field-error notice-form__field-error--hint">{{ shownErrors.target }}</p>
          <p v-else class="notice-form__hint"><i class="fas fa-info-circle"></i> Agency includes agency owners and admins.</p>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 notice-form__field">
          <label class="notice-form__label">Start date <span class="text-danger">*</span></label>
          <el-date-picker
            v-model="form.start_date"
            type="date"
            value-format="yyyy-MM-dd"
            format="MM-dd-yyyy"
            placeholder="Start date"
            :clearable="false"
            :picker-options="startPickerOptions"
            class="notice-form__date"
          ></el-date-picker>
          <p v-if="shownErrors.start_date" class="notice-form__field-error">{{ shownErrors.start_date }}</p>
        </div>
        <div class="col-md-6 notice-form__field">
          <label class="notice-form__label">End date</label>
          <el-date-picker
            v-model="form.end_date"
            type="date"
            value-format="yyyy-MM-dd"
            format="MM-dd-yyyy"
            placeholder="Until dismissed"
            :picker-options="endPickerOptions"
            class="notice-form__date"
          ></el-date-picker>
          <p v-if="shownErrors.end_date" class="notice-form__field-error">{{ shownErrors.end_date }}</p>
        </div>
        <div class="col-12">
          <p class="notice-form__hint notice-form__hint--dates">
            <i class="far fa-clock"></i> Dates follow America/Chicago time. Leave the end date empty to show the notice until users dismiss it.
          </p>
        </div>
      </div>

      <div class="notice-form__field notice-form__active">
        <el-switch v-model="form.is_active"></el-switch>
        <div>
          <span class="notice-form__active-label">Active</span>
          <p class="notice-form__hint mt-0">Turn on to show this notice. Inactive notices are hidden from everyone.</p>
        </div>
      </div>

      <!-- notice global khusus root EMM (Keputusan 16); backend tetap mengecek SYSTEMID -->
      <div v-if="canManageGlobal" class="notice-form__field notice-form__active">
        <el-switch v-model="globalScope"></el-switch>
        <div>
          <span class="notice-form__active-label">Show to all roots (global)</span>
          <p class="notice-form__hint mt-0">Agencies and clients under every root will see this notice.</p>
        </div>
      </div>

      <div v-if="whiteLabelFound.length" class="notice-form__warning">
        <p class="mb-2">
          <i class="fas fa-exclamation-triangle"></i>
          This notice mentions your company name, domain or email.
          {{ whiteLabelAudience.charAt(0).toUpperCase() + whiteLabelAudience.slice(1) }} will see it exactly as written:
        </p>
        <div class="notice-form__terms">
          <mark v-for="term in whiteLabelFound" :key="term">{{ term }}</mark>
        </div>
        <el-checkbox v-model="confirmWhiteLabel">
          I understand this will be visible to {{ whiteLabelAudience }}
        </el-checkbox>
      </div>

      <div v-if="isEdit && dismissedCount > 0" class="notice-form__field notice-form__show-again">
        <el-checkbox v-model="form.show_again">Show again to users who already dismissed</el-checkbox>
        <p class="notice-form__hint">{{ dismissedCount }} {{ dismissedCount === 1 ? 'user has' : 'users have' }} dismissed this notice. They will see it again.</p>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">Cancel</el-button>
      <el-button type="primary" :loading="saving" :disabled="saving || !whiteLabelConfirmed" @click="handleSave">
        {{ isEdit ? 'Save Changes' : 'Add Notice' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { Dialog, Button, Select, Option, Checkbox, DatePicker, Switch, MessageBox } from 'element-ui';
import Editor from '@tinymce/tinymce-vue';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/paste';
import 'tinymce/skins/ui/oxide/skin.min.css';
import { NOTICE_TYPES, detectWhiteLabelTerms, formatNoticeDate, normalizeNoticeText } from '@/helpers/notices';

const TITLE_MAX = 150;

// tanggal kalender dari date picker (waktu lokal) sebagai string YYYY-MM-DD
function pickerDay(time) {
  const pad = value => String(value).padStart(2, '0');
  return `${time.getFullYear()}-${pad(time.getMonth() + 1)}-${pad(time.getDate())}`;
}

function emptyForm() {
  return {
    title: '',
    content: '',
    type: 'info',
    target_agency: false,
    target_client: false,
    // default OFF supaya notice tidak langsung tampil sebelum root yakin isinya benar
    is_active: false,
    start_date: '',
    end_date: '',
    show_again: false,
    // 'own' = root sendiri, 'all' = semua root (hanya root EMM, Keputusan 16)
    root_scope: 'own',
  };
}

/**
 * TASK-EMM-1020: form Add/Edit notice (Keputusan 4, 13, dan 14).
 * Backend tetap penjaga utama: HTML dibersihkan HTMLPurifier dan white-label dicek ulang.
 */
export default {
  name: 'NoticeFormModal',
  components: {
    editor: Editor,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [Select.name]: Select,
    [Option.name]: Option,
    [Checkbox.name]: Checkbox,
    [DatePicker.name]: DatePicker,
    [Switch.name]: Switch,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // null = Add, baris tabel = Edit
    notice: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: emptyForm(),
      types: NOTICE_TYPES,
      editorKey: 0,
      saving: false,
      // error per input baru tampil setelah Save diklik pertama kali, lalu ikut berubah saat diisi
      submitted: false,
      terms: [],
      serverTerms: [],
      confirmWhiteLabel: false,
      // start paling awal (YYYY-MM-DD, Chicago), diisi saat form dibuka; '' = tanpa batas
      minStartDate: '',
      tinymceInit: {
        menubar: false,
        branding: false,
        statusbar: false,
        height: 260,
        skin: false,
        content_css: false,
        plugins: 'link lists paste',
        toolbar: 'undo redo | formatselect | bold italic underline | bullist numlist | link | removeformat',
        block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3',
        // samakan dengan whitelist HTMLPurifier di NoticeContentService.php
        valid_elements: 'p,br,strong/b,em/i,u,ul,ol,li,h1,h2,h3,a[href|target|rel]',
        // default TinyMCE memakai <span style> untuk underline, yang akan dibuang purifier
        formats: {
          underline: { inline: 'u', exact: true },
        },
        default_link_target: '_blank',
        target_list: false,
        link_title: false,
        link_assume_external_targets: true,
        link_default_protocol: 'https',
        // tanpa ini link ke host yang sama menjadi relatif dan rusak saat tampil di domain agency
        convert_urls: false,
        paste_data_images: false,
        content_style:
          'body{margin:12px;font-family:Arial,Helvetica,sans-serif;color:#222;line-height:1.5;font-size:14px;word-break:break-word;overflow-wrap:anywhere;} p{margin:0 0 8px;} h1{font-size:20px;} h2{font-size:18px;} h3{font-size:16px;} h1,h2,h3{margin:0 0 8px;} a{color:#2563eb;}',
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    isEdit() {
      return !!(this.notice && this.notice.id);
    },
    dismissedCount() {
      return this.notice ? Number(this.notice.dismissed_count) || 0 : 0;
    },
    // syarat yang sama dengan menu Report Analytics (routes.js)
    canManageGlobal() {
      return this.$global.rootcomp === true && this.$global.systemUser === true;
    },
    isGlobal() {
      return this.form.root_scope === 'all';
    },
    globalScope: {
      get() {
        return this.isGlobal;
      },
      set(value) {
        this.form.root_scope = value ? 'all' : 'own';
      },
    },
    typeHint() {
      if (this.form.type === 'critical') {
        return 'This notice will appear as a popup to all targets.';
      }
      return 'This notice only appears in the bell. Choose Critical if it must be read.';
    },
    // start sebelum hari ini (Chicago) tidak bisa dipilih; saat edit, tanggal lama tetap boleh (Keputusan 4)
    startPickerOptions() {
      const min = this.minStartDate;
      return {
        disabledDate(time) {
          return !!min && pickerDay(time) < min;
        },
      };
    },
    endPickerOptions() {
      const start = this.form.start_date;
      return {
        disabledDate(time) {
          if (!start) return false;
          return pickerDay(time) < start;
        },
      };
    },
    whiteLabelFound() {
      // notice global juga dibaca agency root lain, jadi dicek untuk semua target (Keputusan 16)
      if (!this.form.target_client && !this.isGlobal) return [];
      const found = detectWhiteLabelTerms(this.terms, this.form.title, this.form.content);
      this.serverTerms.forEach(term => {
        if (found.indexOf(term) === -1) found.push(term);
      });
      return found;
    },
    // pembaca yang disebut di peringatan white-label; notice global mengikuti target yang dicentang
    whiteLabelAudience() {
      if (!this.isGlobal) return 'clients';
      if (this.form.target_agency && this.form.target_client) return 'agencies and clients under every root';
      return this.form.target_agency ? 'agencies under every root' : 'clients under every root';
    },
    fieldErrors() {
      const errors = {};
      const title = this.form.title.trim();
      if (!title) errors.title = 'Title is required.';
      else if (title.length > TITLE_MAX) errors.title = `Title may not be greater than ${TITLE_MAX} characters.`;
      if (normalizeNoticeText(String(this.form.content || '').replace(/<[^>]*>/g, ' ')) === '') errors.content = 'Content is required.';
      if (!this.form.target_agency && !this.form.target_client) errors.target = 'Select at least one target.';
      if (!this.form.start_date) errors.start_date = 'Start date is required.';
      else if (this.minStartDate && this.form.start_date < this.minStartDate) errors.start_date = 'Start date cannot be in the past.';
      if (this.form.start_date && this.form.end_date && this.form.end_date < this.form.start_date) {
        errors.end_date = 'End date must be on or after the start date.';
      }
      return errors;
    },
    shownErrors() {
      return this.submitted ? this.fieldErrors : {};
    },
    whiteLabelConfirmed() {
      return this.whiteLabelFound.length === 0 || this.confirmWhiteLabel;
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.editorKey += 1;
        this.resetForm();
        this.loadTerms();
      }
    },
    // kata yang ditemukan berubah: konfirmasi harus dicentang ulang
    whiteLabelFound(value, oldValue) {
      if (value.join('|') !== (oldValue || []).join('|')) {
        this.confirmWhiteLabel = false;
      }
    },
  },
  methods: {
    resetForm() {
      const form = emptyForm();
      if (this.notice) {
        form.title = this.notice.title || '';
        form.content = this.notice.content || '';
        form.type = this.notice.type || 'info';
        form.target_agency = !!this.notice.target_agency;
        form.target_client = !!this.notice.target_client;
        form.is_active = !!this.notice.is_active;
        form.start_date = this.notice.start_date || '';
        form.end_date = this.notice.end_date || '';
        form.root_scope = this.notice.root_scope === 'all' ? 'all' : 'own';
      } else {
        form.start_date = this.todayInChicago();
      }
      // sama seperti NoticeService::minStartDate(): hari ini, atau tanggal start lama kalau lebih awal
      const today = this.todayInChicago();
      this.minStartDate = form.start_date && today && form.start_date < today ? form.start_date : today;
      // "Show again" selalu tidak dicentang saat form dibuka (Keputusan 13)
      form.show_again = false;
      this.form = form;
      this.submitted = false;
      this.serverTerms = [];
      this.confirmWhiteLabel = false;
      this.saving = false;
    },
    todayInChicago() {
      try {
        return this.$moment().tz('America/Chicago').format('YYYY-MM-DD');
      } catch (e) {
        return '';
      }
    },
    async loadTerms() {
      try {
        this.terms = await this.$store.dispatch('fetchNoticeWhiteLabelTerms');
      } catch (e) {
        // backend tetap mengecek ulang dan membalas 422 kalau perlu konfirmasi
        this.terms = [];
      }
    },
    notifyError(message) {
      this.$notify({ type: 'danger', message, icon: 'fas fa-bug' });
    },
    // pesan create dibedakan menurut status, supaya root sadar notice-nya sudah tampil atau belum
    savedMessage(response) {
      const fallback = (response && response.message) || 'Notice has been saved';
      if (this.isEdit) return fallback;
      if (!this.form.is_active) return "Notice saved as Inactive. Turn it on in the table when it's ready.";

      const today = this.todayInChicago();
      if (!today) return fallback;
      if (this.form.start_date > today) return `Notice created and scheduled to start on ${formatNoticeDate(this.form.start_date)}.`;
      if (this.form.end_date && this.form.end_date < today) return fallback;
      return 'Notice created and is now visible.';
    },
    async confirmShowAgain() {
      try {
        // MessageBox Element, bukan swal: swal muncul di belakang el-dialog
        await MessageBox.confirm(
          `This notice will be shown again to ${this.dismissedCount} ${this.dismissedCount === 1 ? 'user' : 'users'} who already dismissed it.`,
          'Show this notice again?',
          { confirmButtonText: 'Yes, show again', cancelButtonText: 'Cancel', type: 'warning' }
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    async handleSave() {
      this.submitted = true;
      if (Object.keys(this.fieldErrors).length) {
        this.notifyError('Please check the required fields.');
        return;
      }
      if (!this.whiteLabelConfirmed) return;

      if (this.isEdit && this.form.show_again && !(await this.confirmShowAgain())) return;

      this.saving = true;
      try {
        const response = await this.$store.dispatch('saveNotice', {
          id: this.isEdit ? this.notice.id : null,
          title: this.form.title.trim(),
          content: this.form.content,
          type: this.form.type,
          target_agency: this.form.target_agency,
          target_client: this.form.target_client,
          is_active: this.form.is_active,
          start_date: this.form.start_date,
          end_date: this.form.end_date || null,
          root_scope: this.form.root_scope,
          confirm_white_label: this.whiteLabelFound.length > 0 && this.confirmWhiteLabel,
          show_again: this.isEdit && this.form.show_again,
        });
        this.$notify({ type: 'success', message: this.savedMessage(response), icon: 'far fa-save' });
        this.$emit('saved', { created: !this.isEdit });
        this.dialogVisible = false;
      } catch (error) {
        const data = error && error.response && error.response.data ? error.response.data : null;
        if (data && data.code === 'white_label_confirm') {
          // backend menemukan kata root yang belum terdeteksi di browser
          this.serverTerms = data.white_label_terms || [];
          this.confirmWhiteLabel = false;
        }
        this.notifyError((data && data.message) || 'Failed to save notice');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style>
.notice-form-dialog {
  max-width: calc(100vw - 24px);
}
.notice-form-dialog .el-dialog__title {
  font-weight: 600;
}
.notice-form-dialog .el-dialog__body {
  padding-top: 10px;
  padding-bottom: 10px;
}
.notice-form__field {
  margin-bottom: 14px;
}
.notice-form__label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #222a42;
}
.notice-form__hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #6b7280;
}
.notice-form__hint--critical {
  color: #dc2626;
}
.notice-form__hint--dates {
  margin-top: -6px;
  margin-bottom: 18px;
}
/* checkbox bergaris tepi setinggi 40px, sejajar dengan dropdown Type */
.notice-form__checkboxes {
  display: flex;
  gap: 10px;
}
/* dibagi rata supaya tepi kanan sejajar dengan input End date */
.notice-form__checkboxes .el-checkbox.is-bordered {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
}
.notice-form__active {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.notice-form__active .el-switch {
  margin-top: 1px;
}
.notice-form__active-label {
  font-weight: 600;
  color: #222a42;
}
.notice-form__date.el-date-editor.el-input {
  width: 100%;
}
.notice-form__type-option {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
/* keterangan efek Type cukup panjang, jadi boleh turun baris */
.notice-form__type-popper .el-select-dropdown__item {
  height: auto;
  min-height: 34px;
  padding-top: 7px;
  padding-bottom: 7px;
  line-height: 1.4;
  white-space: normal;
}
.notice-form__type-effect {
  color: #6b7280;
  font-size: 12px;
}
.notice-form__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.notice-form__dot--info { background: #3b82f6; }
.notice-form__dot--warning { background: #f59e0b; }
.notice-form__dot--critical { background: #ef4444; }
.notice-form__warning {
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid rgba(245, 158, 11, 0.6);
  border-radius: 6px;
  background: rgba(245, 158, 11, 0.1);
  color: #92400e;
  font-size: 13px;
}
/* _type.scss memberi <p> warna tema (putih pudar di tema gelap), padahal kotak ini selalu terang */
.notice-form__warning p {
  color: inherit;
}
.notice-form__terms {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.notice-form__terms mark {
  padding: 1px 6px;
  border-radius: 4px;
  background: #fde68a;
  color: #78350f;
}
.notice-form__show-again {
  padding-top: 4px;
}
.notice-form__field-error {
  margin: 4px 0 0;
  font-size: 12px;
  color: #dc3545;
}
.notice-form__field-error--hint {
  margin-top: 6px;
}
/* menu TinyMCE (dialog link, dropdown format) harus di atas el-dialog */
.tox-tinymce-aux,
.tox-silver-sink,
.tox .tox-menu {
  z-index: 100000 !important;
}
</style>
